"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { segmentForLocale, swapLang, type Lang } from "@/lib/i18n";
import { cn } from "@/components/ui/cn";

/** Endonym for a language code ("sl" → "slovenščina") for tooltips/AT — the
 *  visible chip stays the compact segment ("SI"). */
export function languageName(langPart: string): string {
  try {
    return new Intl.DisplayNames([langPart], { type: "language" }).of(langPart) ?? langPart;
  } catch {
    return langPart;
  }
}

/** Per-surface colours: marketing frost ("dark" content on the light bar),
 *  marketing over-hero ("light" content on the dark photo), app top bar
 *  ("app"). `shell` styles the bordered pill (segmented control / dropdown
 *  trigger) to sit in the same utility family as the search icon button;
 *  `fill` is the active-language plate; `segHover` the inactive segment. */
const TONES = {
  dark: {
    shell: "border-border/70 text-mid-gray hover:border-foreground/30",
    segHover: "hover:bg-muted hover:text-foreground",
    triggerHover: "hover:text-foreground",
    fill: "bg-gold text-accent-foreground",
    check: "text-gold",
  },
  light: {
    shell: "border-white/30 text-white/85 hover:border-white/60",
    segHover: "hover:bg-white/10 hover:text-white",
    triggerHover: "hover:text-white",
    fill: "bg-gold text-accent-foreground",
    check: "text-gold",
  },
  app: {
    shell: "border-light-gray text-teal hover:border-teal",
    segHover: "hover:bg-light-gray/40",
    triggerHover: "",
    fill: "bg-teal text-white",
    check: "text-teal",
  },
} as const;

/**
 * Language switch, built from the portal's enabled-locale list (O8 —
 * `portal.availableLocales` via `loadPortalConfig().languages`, threaded in
 * by the server chrome). Enabling a locale in admin adds it here with no FE
 * deploy. Reads the live route via usePathname() so it stays correct inside
 * persistent chrome (the header doesn't re-render on client navigation, but
 * this hook does).
 *
 * Two shapes by locale count, both 36px tall so they sit flush with the
 * search icon button and the account/register pills: up to two locales render
 * as ONE segmented pill (one-click switch — the common portal setup); three
 * or more collapse to a globe trigger opening a menu, because a chip per
 * locale is header-breaking width (five chips ≈ 180px next to
 * search/login/register).
 */
export function LangToggle({
  current,
  tone = "light",
  languages,
}: {
  current: Lang;
  tone?: keyof typeof TONES;
  /** Enabled portal locales (e.g. ["sl_SI","en_US","de_DE"]). */
  languages: string[];
}) {
  const pathname = usePathname() ?? `/${current}`;
  const colors = TONES[tone];
  const locales = languages.length > 0 ? languages : ["sl_SI", "en_US"];
  const entries = locales.map((locale) => ({
    locale,
    seg: segmentForLocale(locale),
    langPart: locale.split("_")[0] ?? locale,
  }));

  if (entries.length <= 2) {
    return (
      <span
        role="group"
        aria-label="Language"
        className={cn(
          "inline-flex h-9 items-center gap-0.5 rounded-pill border p-0.5 transition-colors",
          colors.shell,
        )}
      >
        {entries.map(({ locale, seg, langPart }) => (
          <Link
            key={locale}
            href={swapLang(pathname, seg)}
            hrefLang={langPart}
            lang={langPart}
            title={languageName(langPart)}
            aria-current={current === seg ? "true" : undefined}
            className={cn(
              "inline-flex h-full items-center rounded-pill px-2.5 text-xs font-bold transition-colors",
              current === seg ? colors.fill : colors.segHover,
            )}
          >
            {seg}
          </Link>
        ))}
      </span>
    );
  }

  const currentEntry = entries.find((e) => e.seg === current);
  return (
    <Menu as="div" className="relative">
      <MenuButton
        title={currentEntry ? languageName(currentEntry.langPart) : undefined}
        className={cn(
          "group inline-flex h-9 items-center gap-1.5 rounded-pill border px-3 text-xs font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          colors.shell,
          colors.triggerHover,
        )}
      >
        <Globe className="size-3.5" aria-hidden />
        {/* Same hardcoded a11y label as the segmented group above. */}
        <span className="sr-only">Language: </span>
        {current}
        <ChevronDown
          className="size-3 transition-transform duration-150 group-data-[open]:rotate-180"
          aria-hidden
        />
      </MenuButton>
      <Transition
        enter="transition duration-150 ease-out"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-100 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
      >
        <MenuItems
          className="border-border bg-card text-card-foreground absolute right-0 z-30 mt-2 w-52 origin-top-right overflow-hidden rounded-2xl border py-1.5 focus:outline-none"
          style={{ boxShadow: "var(--shadow-lift)" }}
        >
          {entries.map(({ locale, seg, langPart }) => (
            <MenuItem key={locale}>
              {({ focus }) => (
                <Link
                  href={swapLang(pathname, seg)}
                  hrefLang={langPart}
                  lang={langPart}
                  aria-current={current === seg ? "true" : undefined}
                  className={cn(
                    "flex items-center gap-3 px-3.5 py-2.5 text-sm font-bold",
                    focus ? "bg-muted text-foreground" : "text-foreground/80",
                  )}
                >
                  {/* Mini seg plate — the active language carries the same
                      gold/teal fill as the segmented control, so the two
                      shapes of this component share one visual vocabulary. */}
                  <span
                    className={cn(
                      "w-9 shrink-0 rounded-pill border px-1 py-0.5 text-center text-[10px] font-bold tracking-wide",
                      current === seg
                        ? cn("border-transparent", colors.fill)
                        : "border-border text-mid-gray",
                    )}
                  >
                    {seg}
                  </span>
                  <span className="flex-1 truncate capitalize">{languageName(langPart)}</span>
                  {current === seg ? (
                    <Check className={cn("size-4 shrink-0", colors.check)} aria-hidden />
                  ) : null}
                </Link>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
}
